// HospitaliteTab — admin tab to handle Bergerie + Séjour requests in one place.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const STATUS_OPTS = [
  { value: "new",        label: "Nouveau" },
  { value: "contacted",  label: "Contacté" },
  { value: "confirmed",  label: "Confirmé" },
  { value: "declined",   label: "Refusé" },
  { value: "cancelled",  label: "Annulé" },
];

const TYPE_LABELS = {
  retraite_individuelle: "Retraite individuelle",
  journee: "Journée",
  week_end: "Week-end",
  groupe: "Groupe",
  autre: "Autre",
};

export default function HospitaliteTab() {
  const [section, setSection] = useState("bergerie");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = section === "bergerie"
        ? "/admin/bergerie/requests"
        : "/admin/sejour/requests";
      const { data } = await api.get(url);
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (rid, status) => {
    const url = section === "bergerie"
      ? `/admin/bergerie/requests/${rid}`
      : `/admin/sejour/requests/${rid}`;
    await api.patch(url, { status });
    load();
  };

  const remove = async (rid) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    const url = section === "bergerie"
      ? `/admin/bergerie/requests/${rid}`
      : `/admin/sejour/requests/${rid}`;
    await api.delete(url);
    load();
  };

  const purgeTestData = async () => {
    if (!window.confirm("Purger toutes les entrées de test du programme (Test créneau, Pass Test, TEST_Evt) ?")) return;
    setPurgeBusy(true);
    setPurgeMsg("");
    try {
      const { data } = await api.post("/admin/programme/purge-test-data");
      const d = data.deleted || {};
      setPurgeMsg(`Purge effectuée : ${d.presence_schedule || 0} créneaux, ${d.presence_pricing || 0} tarifs, ${d.events || 0} événements supprimés.`);
    } catch (e) {
      setPurgeMsg(`Erreur : ${e?.response?.data?.detail || "échec"}`);
    } finally { setPurgeBusy(false); }
  };

  const exportCsv = () => {
    const rows = [
      ["created_at", "name", "email", "phone", "group", section === "bergerie" ? "nb_people" : "type", section === "bergerie" ? "nb_nights" : "nb_people", "status"],
      ...data.items.map((it) => [
        it.created_at, `${it.first_name} ${it.last_name}`, it.email, it.phone || "",
        it.group_name || "",
        section === "bergerie" ? it.nb_people : (TYPE_LABELS[it.sejour_type] || it.sejour_type),
        section === "bergerie" ? it.nb_nights : it.nb_people,
        it.status,
      ]),
    ];
    const csv = "\ufeff" + rows.map(r => r.map(c => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${section}-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div data-testid="hospitalite-tab" className="space-y-6">
      {/* Sub-tab selector */}
      <div className="flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--border)" }}>
        {[
          { k: "bergerie", lbl: "🛖 Bergerie pèlerine" },
          { k: "sejour",   lbl: "🏛 Séjour au Prieuré" },
        ].map(({ k, lbl }) => (
          <button key={k} data-testid={`hosp-subtab-${k}`}
                  onClick={() => setSection(k)}
                  className="font-caps text-[0.7rem] px-4 py-2.5"
                  style={{
                    background: section === k ? "var(--parchment-2)" : "transparent",
                    borderBottom: section === k ? "2px solid var(--cinnabar)" : "2px solid transparent",
                    letterSpacing: "0.2em",
                  }}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Stats + actions */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-4 text-sm">
          {Object.entries(data.counts || {}).map(([k, v]) => (
            <span key={k} className="font-caps" style={{ color: "var(--muted)", letterSpacing: "0.12em" }}>
              {(STATUS_OPTS.find(o => o.value === k)?.label || k).toUpperCase()} : <strong style={{ color: "var(--ink)" }}>{v}</strong>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn btn-outline" style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem" }}>↻ Rafraîchir</button>
          <button onClick={exportCsv} data-testid="hosp-export-csv" className="btn btn-outline" style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem" }}>↓ Export CSV</button>
        </div>
      </div>

      {/* Maintenance: purge test data */}
      <div className="p-4 border" style={{ borderColor: "var(--border)", background: "var(--parchment-2)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
            <strong>Maintenance :</strong> supprimer les entrées de test du programme (« Test créneau », « Pass Test QR », « TEST_Evt »).
          </div>
          <button data-testid="hosp-purge-test"
                  onClick={purgeTestData} disabled={purgeBusy}
                  className="btn btn-outline"
                  style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem", borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
            {purgeBusy ? "Purge en cours…" : "🧹 Purger les données de test"}
          </button>
        </div>
        {purgeMsg && <div className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>{purgeMsg}</div>}
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center font-body" style={{ color: "var(--muted)" }}>Chargement…</div>
      ) : data.items?.length === 0 ? (
        <div className="py-12 text-center font-body" style={{ color: "var(--muted)" }}>Aucune demande pour le moment.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hosp-table">
            <thead style={{ background: "var(--parchment-2)" }}>
              <tr className="font-caps text-[0.62rem]" style={{ letterSpacing: "0.12em", color: "var(--ink-2)" }}>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Demandeur</th>
                <th className="text-left p-3">Groupe</th>
                <th className="text-left p-3">{section === "bergerie" ? "Pers. / Nuits" : "Type / Pers."}</th>
                <th className="text-left p-3">Dates</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                  <td className="p-3 font-mono text-xs" style={{ color: "var(--muted)" }}>
                    {(it.created_at || "").slice(0, 10)}
                  </td>
                  <td className="p-3">
                    <div className="font-display">{it.first_name} {it.last_name}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{it.email}{it.phone ? ` · ${it.phone}` : ""}</div>
                  </td>
                  <td className="p-3 font-body">{it.group_name || "—"}</td>
                  <td className="p-3 font-body">
                    {section === "bergerie"
                      ? <>{it.nb_people} pers. · {it.nb_nights} nuit(s)</>
                      : <>{TYPE_LABELS[it.sejour_type] || it.sejour_type} · {it.nb_people} pers.</>}
                  </td>
                  <td className="p-3 font-body text-xs" style={{ color: "var(--muted)" }}>
                    {it.arrival_date || "—"}{it.departure_date ? ` → ${it.departure_date}` : ""}
                  </td>
                  <td className="p-3">
                    <select value={it.status} onChange={(e) => updateStatus(it.id, e.target.value)}
                            data-testid={`hosp-status-${it.id}`}
                            className="form-input" style={{ padding: "0.35rem 0.5rem", fontSize: "0.78rem" }}>
                      {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => remove(it.id)} data-testid={`hosp-delete-${it.id}`}
                            className="font-caps text-[0.62rem]"
                            style={{ color: "var(--cinnabar)", letterSpacing: "0.16em", background: "none", border: "none", cursor: "pointer" }}>
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
